import { Feature } from './features';
import { PermissionStatus } from './types';
import { useCallback, useContext } from 'react';
import { GlobalToastMessageContext } from 'libs/callabo-ui/contexts/GlobalToastMessageContext';
import { useTranslate } from './useTranslate';
import { ApiError } from 'next/dist/server/api-utils';
import WorkspaceContext from '@/contexts/WorkspaceContext';
import { PermissionEnum } from '../w/[workspaceId]/components/role/types';

function featureAccess(
  permissions: Partial<Record<PermissionEnum, 'allow' | 'deny' | undefined>> | undefined,
  feature: Feature,
  comparePermission: PermissionStatus = PermissionStatus.ALLOW
): boolean {
  if (!permissions) {
    return false;
  }

  const hasWorkspacePermission = (
    permission: PermissionEnum,
    comparePermission: PermissionStatus
  ) => {
    return permissions[permission] === comparePermission;
  };

  switch (feature) {
    case Feature.LICENSE_PLAN_VIEW:
    case Feature.LICENSE_CARD_REGISTER:
    case Feature.LICENSE_CARD_DELETE:
    case Feature.LICENSE_PLAN_CHANGE:
    case Feature.LICENSE_PAYMENT_HISTORY_VIEW:
      return hasWorkspacePermission(PermissionEnum.PAYMENT_MANAGEMENT, comparePermission);
    case Feature.WORKSPACE_TEAM_CREATE:
    case Feature.WORKSPACE_TEAM_EDIT:
      return hasWorkspacePermission(PermissionEnum.TEAM_MANAGEMENT, comparePermission);
    case Feature.WORKSPACE_NAME_CHANGE:
    case Feature.WORKSPACE_EMAIL_CHANGE:
    case Feature.WORKSPACE_COLOR_CHANGE:
    case Feature.WORKSPACE_SCOPE_CHANGE:
    case Feature.WORKSPACE_MEETING_FILTER_ADD:
    case Feature.WORKSPACE_MEETING_FILTER_DELETE:
    case Feature.WORKSPACE_MEETING_LANGUAGE_CHANGE:
    case Feature.WORKSPACE_KEYWORDS_EDIT:
    case Feature.WORKSPACE_LIFECYCLE_CHANGE:
    case Feature.WORKSPACE_TWO_FACTOR_CHANGE:
    case Feature.WORKSPACE_ACCESS_RULE_CHANGE:
    case Feature.WORKSPACE_SAVE_MEDIA_CHANGE:
    case Feature.WORKSPACE_SAVE_DIALOG_CHANGE:
    case Feature.WORKSPACE_SAML_CONFIG:
    case Feature.WORKSPACE_SCIM_CONFIG:
    case Feature.WORKSPACE_LABEL_CREATE:
    case Feature.WORKSPACE_LABEL_EDIT:
    case Feature.WORKSPACE_LABEL_DELETE:
    case Feature.AUDIT_LOGS_VIEW:
    case Feature.WORKSPACE_RECORD_DELETE_SETTINGS_CHANGE:
    case Feature.WORKSPACE_MAX_DEVICE_SESSION_COUNT_CHANGE:
      return hasWorkspacePermission(PermissionEnum.WORKSPACE_MANAGEMENT, comparePermission);
    case Feature.WORKSPACE_INVITEE_VIEW:
    case Feature.WORKSPACE_USER_ADD:
    case Feature.WORKSPACE_USER_ROLE_EDIT:
    case Feature.WORKSPACE_JOIN_REQUEST_VIEW:
      return hasWorkspacePermission(PermissionEnum.MEMBER_MANAGEMENT, comparePermission);
    case Feature.WORKSPACE_TEAM_MEMBER_ADD:
    case Feature.WORKSPACE_TEAM_MEMBER_DELETE:
    case Feature.WORKSPACE_TEAM_MEMBER_VIEW:
      return hasWorkspacePermission(PermissionEnum.TEAM_MEMBER_MANAGEMENT, comparePermission);
    case Feature.MEETING_DELETE:
      return hasWorkspacePermission(
        PermissionEnum.DELETE_MEETINGS_FROM_WORKSPACE,
        comparePermission
      );
    case Feature.MEETING_SHARE_SET:
      return hasWorkspacePermission(PermissionEnum.EXTERNAL_MEETING_SHARING, comparePermission);
    case Feature.AUTOMATION_SLACK_CHANNEL:
      return hasWorkspacePermission(PermissionEnum.WORKSPACE_INTEGRATION, comparePermission);
    case Feature.AUTOMATION_SLACK_CHANNEL_FOR_TEAM:
      return hasWorkspacePermission(PermissionEnum.AUTOMATION_INTEGRATION, comparePermission);
    case Feature.INTEGRATION_CALENDAR_GOOGLE:
    case Feature.INTEGRATION_CALENDAR_OUTLOOK:
    case Feature.MEETING_ARCHIVE:
    case Feature.MEETING_DOWNLOAD_MP4:
      return hasWorkspacePermission(PermissionEnum.ARCHIVE_MEETINGS, comparePermission);
    case Feature.MEETING_SUMMARY_EDIT:
    case Feature.MEETING_LABEL_CHANGE:
    case Feature.MEETING_TITLE_CHANGE:
    case Feature.MEETING_DIALOG_EDIT:
    case Feature.MEETING_BOOKMARK_CHANGE:
    case Feature.MEETING_PARTICIPANT_NAME_CHANGE:
    case Feature.MEETING_PROTECT:
    case Feature.MEETING_INSIGHT_TRANSLATION:
    case Feature.MEETING_CHAT:
    case Feature.MEETING_EDIT_CUSTOM_INSIGHT:
      return hasWorkspacePermission(PermissionEnum.EDIT_MEETING_PROPERTIES, comparePermission);
    case Feature.MEETING_LANGUAGE_RESET:
      return hasWorkspacePermission(PermissionEnum.RESET_LANGUAGE, comparePermission);
    case Feature.MEETING_ACCESS_CHANGE:
      return hasWorkspacePermission(PermissionEnum.EDIT_MEETING_VIEW_SCOPE, comparePermission);
    case Feature.MEETING_EXPORT_SALESFORCE:
    case Feature.MEETING_EXPORT_PIPEDRIVE:
    case Feature.MEETING_EXPORT_TEAMS:
    case Feature.MEETING_EXPORT_SLACK:
    case Feature.MEETING_EXPORT_NOTION:
    case Feature.MEETING_EXPORT_EMAIL:
    case Feature.INTEGRATION_CRM_SALESFORCE:
    case Feature.INTEGRATION_CRM_PIPEDRIVE:
    case Feature.INTEGRATION_TEAMS:
    case Feature.INTEGRATION_NOTION:
    case Feature.INTEGRATION_SLACK_UNFURL:
    case Feature.INTEGRATION_CONFLUENCE:
    case Feature.MEETING_EXPORT_CONFLUENCE:
    case Feature.INTEGRATION_ZAPIER:
    case Feature.INTEGRATION_LINEAR:
      return hasWorkspacePermission(PermissionEnum.SHARE_INTEGRATION, comparePermission);
    case Feature.MEETING_VIEW:
      return hasWorkspacePermission(PermissionEnum.VIEW_MEETINGS, comparePermission);
    case Feature.ALL_TEAM_ACCESS:
    case Feature.ALL_MEETING_ARCHIVE:
      return hasWorkspacePermission(PermissionEnum.ALL_ARCHIVE_MEETINGS, comparePermission);
    case Feature.MEETING_DELETE_FROM_TEAM:
      return hasWorkspacePermission(PermissionEnum.EDIT_MEETING_VIEW_SCOPE, comparePermission);
    case Feature.LICENSE_PAYMENT_MANAGEMENT:
      return hasWorkspacePermission(PermissionEnum.PAYMENT_MANAGEMENT, comparePermission);
    default:
      throw new Error('아직 구현중인 기능입니다. ' + feature);
  }
}

export const useFeatureAccess = () => {
  const { me } = useContext(WorkspaceContext);

  return useCallback(
    (feature: Feature) => {
      return featureAccess(me?.workspacePermissions, feature);
    },
    [me]
  );
};

export const useTeamFeatureAccess = () => {
  const { me } = useContext(WorkspaceContext);

  return useCallback(
    (feature: Feature, teamId: number): boolean => {
      const filteredTeamMember = me.teamMembers.find((tm) => tm.team.id === teamId);
      if (featureAccess(filteredTeamMember?.roleTeamPermissions, feature, PermissionStatus.DENY)) {
        return false;
      }

      return featureAccess(filteredTeamMember?.roleTeamPermissions, feature);
    },
    [me]
  );
};

export const useCustomFeatureAccess = (
  recordPermissions: Partial<Record<PermissionEnum, 'allow' | 'deny' | undefined>>
) => {
  return useCallback(
    (feature: Feature) => {
      return recordPermissions ? featureAccess(recordPermissions, feature) : false;
    },
    [recordPermissions]
  );
};

export const useConditionalAccess = () => {
  const { toast } = useContext(GlobalToastMessageContext);
  const { t } = useTranslate();
  const hasFeatureAccess = useFeatureAccess();

  return useCallback(
    async (feature: Feature, fn: () => Promise<void>) => {
      if (hasFeatureAccess(feature)) {
        try {
          await fn();
        } catch (error) {
          if (error instanceof ApiError && error.statusCode === 403) {
            toast(t.common.ERROR_NO_PERMISSION);
          }
        }
      } else {
        toast(t.common.ERROR);
      }
    },
    [hasFeatureAccess]
  );
};
